import api from './client';

// Types pour les statistiques du dashboard
export interface DashboardStats {
  total_employees: number;
  active_employees: number;
  on_leave_today: number;
  contracts_ending_soon: number; // Dans les 30 prochains jours
  new_employees_this_month: number;
  contracts_by_type: {
    CDI: number;
    CDD: number;
    STAGE: number;
  };
}

export interface BirthdayEmployee {
  id: number;
  full_name: string;
  date_of_birth: string;
  department_name: string;
}

export interface ContractEndingSoon {
  id: number;
  contract_id?: number | null;
  employee_name: string;
  employee_id: string;
  department_name?: string;
  contract_start_date?: string;
  contract_end_date: string;
  days_remaining: number;
  photo?: string;
  position_display?: string;
  email?: string;
}

// API Calls
export async function fetchDashboardStats(): Promise<DashboardStats> {
  // Volontairement sans filet : une erreur doit remonter à l'écran. Renvoyer
  // des zéros silencieux laissait croire à une base vide.
  {
    const { data } = await api.get('employees/employees/dashboard_stats/');
    return {
      total_employees: data?.total_employees ?? 0,
      active_employees: data?.active_employees ?? 0,
      on_leave_today: data?.on_leave_today ?? 0,
      contracts_ending_soon: data?.contracts_ending_soon ?? 0,
      new_employees_this_month: data?.new_employees_this_month ?? 0,
      contracts_by_type: {
        CDI: data?.contracts_by_type?.CDI ?? 0,
        CDD: data?.contracts_by_type?.CDD ?? 0,
        STAGE: data?.contracts_by_type?.STAGE ?? 0,
      },
    };
  }
}

export async function fetchMonthlyBirthdays(): Promise<BirthdayEmployee[]> {
  const { data } = await api.get('employees/employees/upcoming_birthdays/?days=30');
  return data.map((emp: any) => ({
    id: emp.id,
    full_name: emp.full_name,
    date_of_birth: emp.date_of_birth,
    department_name: emp.department_name
  }));
}

export async function fetchContractsEndingSoon(): Promise<ContractEndingSoon[]> {
  try {
    const { data } = await api.get('employees/employees/expiring_contracts/');
    console.log('📋 Contrats expirant bientôt (réponse API):', data);

    // Gérer les réponses paginées et non-paginées
    const employees = Array.isArray(data) ? data : (data.results || []);

    if (!Array.isArray(employees)) {
      console.warn('⚠️ Impossible d\'extraire les données:', data);
      return [];
    }

    const contracts = employees
      .filter((emp: any) => emp.contract_end_date)
      .map((emp: any) => {
        const endDate = new Date(emp.contract_end_date);
        const today = new Date();
        const diff = endDate.getTime() - today.getTime();
        const daysRemainingCalc = diff > 0 ? Math.ceil(diff / (1000 * 60 * 60 * 24)) : 0;
        return {
          id: emp.id,
          contract_id: emp.contract_id || null,
          employee_name: emp.full_name,
          employee_id: emp.employee_id,
          department_name: emp.department_name,
          contract_start_date: emp.contract_start_date,
          contract_end_date: emp.contract_end_date,
          days_remaining: daysRemainingCalc,
          photo: emp.photo,
          position_display: emp.position_display,
          email: emp.email,
        };
      });

    console.log(`✅ ${contracts.length} contrat(s) expirant dans les 30 jours`);
    return contracts;
  } catch (error) {
    console.error('❌ Erreur lors de la récupération des contrats:', error);
    return [];
  }
}

export async function fetchExpiredContracts(): Promise<ContractEndingSoon[]> {
  try {
    const { data } = await api.get('employees/employees/expired_contracts/');
    console.log('📋 Contrats expirés (réponse API):', data);

    const employees = Array.isArray(data) ? data : (data.results || []);

    if (!Array.isArray(employees)) {
      console.warn('⚠️ Impossible d\'extraire les données:', data);
      return [];
    }

    const contracts = employees
      .filter((emp: any) => emp.contract_end_date)
      .map((emp: any) => {
        const endDate = new Date(emp.contract_end_date);
        const today = new Date();
        const diff = endDate.getTime() - today.getTime();
        const daysRemainingCalc = Math.ceil(diff / (1000 * 60 * 60 * 24));
        return {
          id: emp.id,
          contract_id: emp.contract_id || null,
          employee_name: emp.full_name,
          employee_id: emp.employee_id,
          department_name: emp.department_name,
          contract_start_date: emp.contract_start_date,
          contract_end_date: emp.contract_end_date,
          days_remaining: daysRemainingCalc,
          photo: emp.photo,
          position_display: emp.position_display,
          email: emp.email,
        };
      });

    console.log(`✅ ${contracts.length} contrat(s) expirés`);
    return contracts;
  } catch (error) {
    console.error('❌ Erreur lors de la récupération des contrats expirés:', error);
    return [];
  }
}

// ---------------------------------------------------------------------------
// Stats détaillées — réutilise l'endpoint existant employees/employees/stats/
// (aucun ajout backend : ce endpoint renvoie déjà tout ce qui suit)
// ---------------------------------------------------------------------------

export type CompanyCount = { company__name: string | null; count: number };

export interface EmployeeStats {
  total_employees: number;
  active_employees: number;
  inactive_employees: number;
  on_leave: number;
  terminated: number;
  by_company: CompanyCount[];
  total_salary_mass: number;
}

export async function fetchEmployeeStats(): Promise<EmployeeStats> {
  {
    const { data } = await api.get('employees/employees/stats/');
    return {
      total_employees: data?.total_employees ?? 0,
      active_employees: data?.active_employees ?? 0,
      inactive_employees: data?.inactive_employees ?? 0,
      on_leave: data?.on_leave ?? 0,
      terminated: data?.terminated ?? 0,
      by_company: Array.isArray(data?.by_company) ? data.by_company : [],
      total_salary_mass: data?.total_salary_mass ?? 0,
    };
  }
}
